import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  HUD,
  HUDDirection,
  HUDMainItemMode,
  MAIN_ITEM_ID,
  type HUDContextValues,
} from '@ole-ui/ole-ui-components';
import { createPortal } from 'react-dom';
import GripVerticalIcon from '@patternfly/react-icons/dist/esm/icons/grip-vertical-icon';

import resizeIconUrl from 'img/icons/resize_vertical.svg';
import type AIAssistantCourseStore from 'components/Course/AiAssist/store';
import CourseToC from './CourseToC';
import type CourseStore from './store';
import AiAssistSidebar from './AiAssistSidebar';
import useAiAssistPanelResize from './hooks/useAiAssistPanelResize';

const TOC_PANEL_WIDTH = 340;

export function syncPanelOffsets(isHudAtTop: boolean) {
  const hudEl = document.querySelector<HTMLElement>('.hud-course-view__hud');
  if (!hudEl) return;
  const { top, bottom } = hudEl.getBoundingClientRect();
  const root = document.documentElement;
  if (isHudAtTop) {
    root.style.setProperty('--panel-top-offset', `${Math.max(0, bottom)}px`);
    root.style.setProperty('--panel-bottom-offset', '0px');
  } else {
    root.style.removeProperty('--panel-top-offset');
    root.style.setProperty(
      '--panel-bottom-offset',
      `${Math.max(0, window.innerHeight - top)}px`,
    );
  }
}

type Props = {
  courseStore: CourseStore;
  isOverviewDisabled: boolean;
  onOverviewButtonClicked: () => void;
  sideToCRef?: React.RefObject<HTMLDivElement>;
  isSideToCActive?: boolean;
  setSideToCActive?: (isActive: boolean) => void;
  aiAssistRef?: React.RefObject<HTMLDivElement>;
  aiAssistantStore: AIAssistantCourseStore | null;
  isAiAssistSidebarOpen: boolean;
  tocModeRef?: React.MutableRefObject<HUDMainItemMode>;
  sidebarPosition?: 'left' | 'right';
};

type RestoreSidebar = 'toc' | 'ai' | null;

type CourseHUDPortalsContentProps = Props & {
  contextValues: HUDContextValues;
};

const CourseHUDPortalsContent = ({
  contextValues,
  courseStore,
  isOverviewDisabled,
  onOverviewButtonClicked,
  sideToCRef,
  isSideToCActive,
  setSideToCActive,
  aiAssistRef,
  aiAssistantStore,
  isAiAssistSidebarOpen,
  tocModeRef,
  sidebarPosition = 'left',
}: CourseHUDPortalsContentProps) => {
  const { activeItem, direction, isMobileMode, mainItemMode, setActiveItem } =
    contextValues;
  const isHudAtTop = direction === HUDDirection.DOWNWARD;

  // Keep the latest mode available to the HUD MainItem toggle handler (CourseHUD).
  if (tocModeRef) {
    // eslint-disable-next-line no-param-reassign
    tocModeRef.current = mainItemMode;
  }

  const isExternalTocMode =
    mainItemMode === HUDMainItemMode.EXTERNAL && !isMobileMode;
  const isTocHudOpen = activeItem === MAIN_ITEM_ID;
  const isTocSidebarOpen = Boolean(isExternalTocMode && isSideToCActive);

  // Full view should behave like Sidebar today (mutual exclusion with AI).
  const shouldCoordinateFullView =
    !isMobileMode && mainItemMode === HUDMainItemMode.FULL;

  // Ensure the external sidebar ToC state machine can't get stuck "on" when
  // the ToC is rendered as a HUD panel (Full view / Minimized).
  useEffect(() => {
    if (!setSideToCActive) return;
    if (!isExternalTocMode && isSideToCActive) {
      setSideToCActive(false);
    }
  }, [isExternalTocMode, isSideToCActive, setSideToCActive]);

  const [restoreSidebarAfterClose, setRestoreSidebarAfterClose] =
    useState<RestoreSidebar>(null);

  const prevAiSidebarOpenRef = useRef(isAiAssistSidebarOpen);
  const prevTocHudOpenRef = useRef(isTocHudOpen);

  // Reset any queued restores when we leave Full view.
  useEffect(() => {
    if (shouldCoordinateFullView) return;
    if (restoreSidebarAfterClose !== null) {
      setRestoreSidebarAfterClose(null);
    }
  }, [shouldCoordinateFullView, restoreSidebarAfterClose]);

  // AI sidebar open/close transitions when ToC mode is Full view.
  useEffect(() => {
    const wasOpen = prevAiSidebarOpenRef.current;

    if (!shouldCoordinateFullView) {
      prevAiSidebarOpenRef.current = isAiAssistSidebarOpen;
      return;
    }

    // AI sidebar just opened while ToC panel is open -> close ToC and remember to restore.
    if (!wasOpen && isAiAssistSidebarOpen && isTocHudOpen) {
      setRestoreSidebarAfterClose('toc');
      setActiveItem(null);
    }

    // AI sidebar just closed -> restore ToC panel if it was displaced, but only
    // if the user hasn't opened another HUD panel in the meantime.
    if (
      wasOpen &&
      !isAiAssistSidebarOpen &&
      restoreSidebarAfterClose === 'toc'
    ) {
      if (activeItem === null) {
        setActiveItem(MAIN_ITEM_ID);
      }
      setRestoreSidebarAfterClose(null);
    }

    prevAiSidebarOpenRef.current = isAiAssistSidebarOpen;
  }, [
    activeItem,
    isAiAssistSidebarOpen,
    isTocHudOpen,
    restoreSidebarAfterClose,
    setActiveItem,
    shouldCoordinateFullView,
  ]);

  // ToC panel open/close transitions when ToC mode is Full view.
  useEffect(() => {
    const wasOpen = prevTocHudOpenRef.current;

    if (!shouldCoordinateFullView) {
      prevTocHudOpenRef.current = isTocHudOpen;
      return;
    }

    if (!aiAssistantStore) {
      prevTocHudOpenRef.current = isTocHudOpen;
      return;
    }

    // ToC panel just opened while AI is open -> close AI and remember to restore.
    if (!wasOpen && isTocHudOpen && isAiAssistSidebarOpen) {
      setRestoreSidebarAfterClose('ai');
      aiAssistantStore.setIsOpen(false);
    }

    // ToC panel just closed -> restore AI if it was displaced.
    if (wasOpen && !isTocHudOpen && restoreSidebarAfterClose === 'ai') {
      aiAssistantStore.setIsOpen(true);
      setRestoreSidebarAfterClose(null);
    }

    prevTocHudOpenRef.current = isTocHudOpen;
  }, [
    aiAssistantStore,
    isAiAssistSidebarOpen,
    isTocHudOpen,
    restoreSidebarAfterClose,
    shouldCoordinateFullView,
  ]);

  const tocPortal =
    sideToCRef?.current && isExternalTocMode && isSideToCActive
      ? createPortal(
          <div className="hud-course-view__side-toc-wrapper">
            <div className="hud-course-view__side-toc pf-v5-theme-dark">
              <CourseToC
                asSidebar
                courseStore={courseStore}
                isOverviewDisabled={isOverviewDisabled}
                onOverviewButtonClicked={onOverviewButtonClicked}
              />
            </div>
            <div className="hud-course-view__side-toc-shadow" />
          </div>,
          sideToCRef.current,
        )
      : null;

  // In Minimized mode, allow the ToC and AI sidebar to coexist.
  // In Sidebar and Full view, keep them mutually exclusive.
  const isMutuallyExclusiveTocMode =
    mainItemMode === HUDMainItemMode.EXTERNAL ||
    mainItemMode === HUDMainItemMode.FULL;

  const isTocOpenForMutualExclusion =
    mainItemMode === HUDMainItemMode.EXTERNAL ? isTocSidebarOpen : isTocHudOpen;

  const shouldHideAiForToc =
    isMutuallyExclusiveTocMode && isTocOpenForMutualExclusion;

  const { panelWidth, isDragging, onDragHandleMouseDown } =
    useAiAssistPanelResize({ dragCursorUrl: resizeIconUrl });

  const isPanelVisible =
    !!aiAssistantStore &&
    isAiAssistSidebarOpen &&
    !shouldHideAiForToc &&
    !isMobileMode;

  const ANIMATION_MS = 200;
  type PanelPhase = 'hidden' | 'opening' | 'visible' | 'closing';
  const [panelPhase, setPanelPhase] = useState<PanelPhase>(
    isPanelVisible ? 'visible' : 'hidden',
  );

  // Drive open/close phases from visibility — never setState during render.
  useEffect(() => {
    if (isMobileMode) {
      setPanelPhase('hidden');
      return;
    }

    setPanelPhase((current) => {
      if (isPanelVisible) {
        return current === 'hidden' || current === 'closing'
          ? 'opening'
          : current;
      }
      return current === 'visible' || current === 'opening'
        ? 'closing'
        : current;
    });
  }, [isMobileMode, isPanelVisible]);

  // Advance 'opening' → 'visible' and 'closing' → 'hidden' after the animation.
  useEffect(() => {
    if (isMobileMode) return undefined;
    if (panelPhase !== 'opening' && panelPhase !== 'closing') return undefined;
    const next = panelPhase === 'opening' ? 'visible' : 'hidden';
    const timer = setTimeout(() => setPanelPhase(next), ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [isMobileMode, panelPhase]);

  // Inset course content when panels are open. HUD escapes this padding via CSS.
  useLayoutEffect(() => {
    const tocPad = isExternalTocMode && isSideToCActive ? TOC_PANEL_WIDTH : 0;
    const aiPad = !isMobileMode && panelPhase !== 'hidden' ? panelWidth : 0;

    document.documentElement.style.setProperty(
      '--content-padding-left',
      `${sidebarPosition === 'left' ? tocPad : 0}px`,
    );
    document.documentElement.style.setProperty(
      '--content-padding-right',
      `${sidebarPosition === 'right' ? tocPad : aiPad}px`,
    );

    syncPanelOffsets(isHudAtTop);

    return () => {
      document.documentElement.style.removeProperty('--content-padding-left');
      document.documentElement.style.removeProperty('--content-padding-right');
    };
  }, [
    isExternalTocMode,
    isHudAtTop,
    isMobileMode,
    isSideToCActive,
    sidebarPosition,
    panelPhase,
    panelWidth,
  ]);

  useEffect(() => {
    const update = () => {
      const masthead = document.querySelector<HTMLElement>('.pf-v5-c-masthead');
      if (!masthead) return;
      const { bottom } = masthead.getBoundingClientRect();
      document.documentElement.style.setProperty(
        '--header-height',
        `${Math.max(0, bottom)}px`,
      );
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  const tracksHudPosition =
    !isMobileMode &&
    ((isExternalTocMode && isSideToCActive) || panelPhase !== 'hidden');

  // Sync every frame while a side panel is open — scroll events lag behind fast scroll.
  useEffect(() => {
    if (!tracksHudPosition) {
      document.documentElement.style.removeProperty('--panel-top-offset');
      document.documentElement.style.removeProperty('--panel-bottom-offset');
      return undefined;
    }

    let rafId = 0;
    let lastKey = '';
    const tick = () => {
      const hudEl = document.querySelector<HTMLElement>(
        '.hud-course-view__hud',
      );
      if (hudEl) {
        const { top, bottom } = hudEl.getBoundingClientRect();
        const key = `${isHudAtTop}:${top}:${bottom}`;
        if (key !== lastKey) {
          lastKey = key;
          syncPanelOffsets(isHudAtTop);
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      document.documentElement.style.removeProperty('--panel-top-offset');
      document.documentElement.style.removeProperty('--panel-bottom-offset');
    };
  }, [isHudAtTop, tracksHudPosition]);

  const aiAssistPortal =
    panelPhase !== 'hidden' && aiAssistRef?.current
      ? createPortal(
          <div
            className={`hud-course-view__ai-assist-wrapper${
              panelPhase === 'closing'
                ? ' hud-course-view__ai-assist-wrapper--closing'
                : ''
            }`}
            style={{ width: panelWidth, minWidth: panelWidth }}
          >
            <button
              type="button"
              className={`hud-course-view__ai-assist-drag-handle${
                isDragging
                  ? ' hud-course-view__ai-assist-drag-handle--dragging'
                  : ''
              }`}
              onMouseDown={onDragHandleMouseDown}
              style={{
                ['--ai-assist-drag-cursor' as string]: `url(${resizeIconUrl}) 12 12, ew-resize`,
              }}
              aria-label="Resize learning assistant panel"
              data-analytics-id="ai-assist-panel-resize-handle"
            >
              <GripVerticalIcon className="hud-course-view__ai-assist-drag-icon hud-course-view__ai-assist-drag-icon--grip" />
            </button>
            <div className="hud-course-view__ai-assist-panel pf-v5-theme-dark">
              <AiAssistSidebar aiAssistantStore={aiAssistantStore} />
            </div>
            <div className="hud-course-view__ai-assist-shadow" />
          </div>,
          aiAssistRef.current,
        )
      : null;

  return (
    <>
      {tocPortal}
      {aiAssistPortal}
    </>
  );
};

const CourseHUDPortals = (props: Props) => (
  <HUD.Portal>
    {(contextValues) => (
      <CourseHUDPortalsContent {...props} contextValues={contextValues} />
    )}
  </HUD.Portal>
);

export default CourseHUDPortals;
